import React, { ReactNode } from 'react';
import {
  CreditCardHolderDetails,
  CreditCardNumber,
  CreditCardAddress,
} from '../../molecules';

import { Box, Logo } from '../../atoms';
import { Chip, StatusMsg, Watermark } from './components';
import {
  AddressWrapper,
  Card,
  CardNumberWrapper,
  Row,
} from './CreditCard.styles';

import { formatAddressDetails } from './utils';
import { AddressDetails, CardDetails, Size, Status, Variant } from './types';

export type CreditCardProps = {
  /**
   * Credit card details
   */
  card: CardDetails;
  /**
   * Address details
   */
  addressDetails?: AddressDetails;
  /**
   * Credit card variant
   */
  variant?: Variant;
  /**
   * Credit card size
   */
  size?: Size;
  /**
   * Credit card status
   */
  status?: Status;
  /**
   * Credit card render slot function
   */
  renderSlot?: () => ReactNode;
  /**
   * Credit card status message
   */
  statusMsg?: string;
};

/**
- Credit card component used for displaying credit card details.
**/
const CreditCard = ({
  card,
  addressDetails = {},
  variant = 'primary',
  size = 'regular',
  status,
  renderSlot,
}: CreditCardProps) => {
  const {
    type,
    maskedNumber,
    firstName,
    lastName,
    expirationMonth,
    expirationYear,
  } = card;

  const { firstLine, secondLine } = formatAddressDetails(addressDetails);

  return (
    <>
      <Card
        variant={variant}
        size={size}
        status={status}
        data-testid="creditCard"
      >
        <Watermark type={type} size={size} />
        <Row gridGap={size === 'small' ? 3 : 5} display={'flex'}>
          <Chip />
          <AddressWrapper>
            <CreditCardAddress firstLine={firstLine} secondLine={secondLine} />
          </AddressWrapper>
          {renderSlot && <div>{renderSlot()}</div>}
        </Row>
        <CardNumberWrapper>
          <CreditCardNumber number={maskedNumber} />
        </CardNumberWrapper>
        <Row
          gridGap={size === 'small' ? 2 : 3}
          gridTemplateColumns="1fr auto auto"
        >
          <CreditCardHolderDetails
            firstName={firstName}
            lastName={lastName}
            expirationMonth={expirationMonth}
            expirationYear={expirationYear}
          />
          <Logo type={type} />
        </Row>
      </Card>
      {status && (
        <Box marginTop={3}>
          <StatusMsg status={status} />
        </Box>
      )}
    </>
  );
};

export default Object.assign(CreditCard, {
  Watermark,
  Chip,
  Logo,
});
